import { useApolloClient } from '@apollo/react-hooks';
import { useSessionData } from '@confluence/session-data';
import { usePageContentId } from '@confluence/page-context';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

import {
	LastEditedVersionFragment,
	LastEditedDraftVersionFragment,
} from './LastEditedVersionFragments.fragment';

// if isOnEditRoute, updates the draft timestamp cache for edit page, edit blog, and drafts if you are currently editing that piece of content (i.e. change in editor content or rename title from quick actions while on editor of that page)
// otherwise, update published version history cache for the given content as all content types use published version history except for edit page, edit blog, & drafts
export const useUpdateLastEditedTimestampVersionCache = () => {
	const apolloClient = useApolloClient();
	const { displayName } = useSessionData();
	const [currentPageContentId] = usePageContentId();
	const isOnEditRoute = useIsEditorPage();

	const updateTimestampCache = ({ contentId }: { contentId: string }) => {
		const newWhen = new Date().toISOString();

		const isEditingThisContent = isOnEditRoute && contentId === currentPageContentId;
		const fragment = isEditingThisContent
			? LastEditedDraftVersionFragment
			: LastEditedVersionFragment;
		const id = `$Content:${contentId}.${isEditingThisContent ? 'draftVersion' : 'version'}`;

		let prevTypename = 'KnownUser';

		try {
			const prevCache = apolloClient.readFragment({ id, fragment });
			if (prevCache?.by?.__typename) {
				prevTypename = prevCache.by.__typename;
			}
		} catch {
			// silently fallback to default typename "KnownUser"
		}

		try {
			apolloClient.writeFragment({
				id,
				fragment,
				data: {
					by: {
						displayName,
						__typename: prevTypename,
					},
					when: newWhen,
					__typename: 'Version',
				},
			});
		} catch {
			// silently fail write to cache
		}
	};

	return updateTimestampCache;
};
